import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface DivisionCard {
  title: string;
  description: string;
  icon: string;
  link: string;
  image: string;
}

const divisions: DivisionCard[] = [
  {
    title: 'Telecommunications',
    description:
      'A&E and management services for the wireless and wireline telecommunications industry. Field services, engineering & design, structural engineering, and more across 20+ states.',
    icon: 'ri-signal-tower-line',
    link: '/telecommunications',
    image:
      'https://storage.readdy-site.link/project_files/e0b555b8-3b68-4e41-a4cb-a02f4236e8ac/8d175163-80d3-4cab-b5a6-f7f8784c1914_tower.png?v=6b967c6308261eb55d40d81f1466c64e',
  },
  {
    title: 'Land Services',
    description:
      'Professional land management and acquisition services. Site procurement, lease negotiations, zoning compliance, and property management for infrastructure development projects.',
    icon: 'ri-landscape-line',
    link: 'https://holstonlandmgmt.com',
    image:
      'https://storage.readdy-site.link/project_files/e0b555b8-3b68-4e41-a4cb-a02f4236e8ac/ede0619d-fc61-47fe-bb88-aea5813f3453_equip-and-clearing.png?v=bfd15e221c1463763f058f5717d93eae',
  },
];

export default function DivisionsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = sectionRef.current?.querySelectorAll('.fade-in-child');
            children?.forEach((child, i) => {
              const el = child as HTMLElement;
              setTimeout(() => {
                el.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
                el.style.opacity = '1';
                el.style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 lg:py-28 bg-[#E8EFF6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p
            className="fade-in-child text-[#0050B2] text-sm font-semibold uppercase tracking-widest mb-3"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            Our Divisions
          </p>
          <h2
            className="fade-in-child text-3xl lg:text-4xl font-bold text-[#001F4E]"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            Specialized Expertise,
            <br />
            <span className="text-[#0050B2]">One Unified Group</span>
          </h2>
          <p
            className="fade-in-child text-gray-600 mt-4 text-base"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            Holston Development Group operates through focused divisions, each delivering
            deep industry expertise under a unified leadership structure.
          </p>
        </div>

        {/* Division Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
          {divisions.map((div, i) => (
            <div
              key={i}
              className="fade-in-child bg-white rounded-lg overflow-hidden group cursor-pointer"
              style={{ opacity: 0, transform: 'translateY(20px)' }}
            >
              <div className="relative overflow-hidden h-48">
                <img
                  src={div.image}
                  alt={div.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#001F4E]/60 to-transparent" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-md bg-[#E8EFF6]">
                    <i className={`${div.icon} text-lg text-[#0050B2]`} />
                  </div>
                  <h3 className="text-lg font-bold text-[#001F4E]">{div.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{div.description}</p>
                {div.link.startsWith('http') ? (
                  <a
                    href={div.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#0050B2] hover:text-[#001F4E] transition-colors cursor-pointer"
                  >
                    Explore More
                    <i className="ri-external-link-line" />
                  </a>
                ) : (
                  <Link
                    to={div.link}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#0050B2] hover:text-[#001F4E] transition-colors cursor-pointer"
                  >
                    Explore More
                    <i className="ri-arrow-right-line" />
                  </Link>
                )}
              </div>
            </div>
          ))}

          {/* Placeholder - more coming */}
          <div
            className="fade-in-child bg-white/60 rounded-lg border-2 border-dashed border-[#0050B2]/20 p-8 flex flex-col items-center justify-center text-center min-h-[320px]"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E8EFF6] mb-4">
              <i className="ri-add-line text-2xl text-[#0050B2]" />
            </div>
            <p className="text-[#001F4E] font-semibold text-sm mb-1">More Divisions Coming</p>
            <p className="text-gray-400 text-xs leading-relaxed">
              Holston Development Group continues to grow with new specialized divisions.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
